import { AccountSecurityModule } from "./interfaces";
import { DeactivateModal } from "./TwoFactorAuthentication/Components/DeactivateModal/DeactivateModal";
import { EventNames } from "core/monitoring/types/enums";
import { ReactComponent as BiometricLoginIcon } from "@icons/biometric-login.svg";
import { ReactComponent as PasswordManagementIcon } from "@icons/password-management.svg";
import { ReactComponent as OrganizationCodeIcon } from "@icons/organization-code.svg";
import { RecoveryCodesModal } from "./TwoFactorAuthentication/Components/RecoveryCodesModal/RecoveryCodesModal";
import { ReactComponent as SingleSignOnIcon } from "@icons/single-sign-on.svg";
import { ReactComponent as TwoFactorIcon } from "@icons/two-factor.svg";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTwoFactorAuthSetup } from "shared/core/hooks/useTwoFactorAuth";
import { ClientInfoState } from "shared/core/state/clientInfoState";
import { useLocation } from "react-router-dom";
import { DestinationState } from "shared/core/state/destinationState";
import { useAuthVersioning } from "core/hooks/useAuthVersioning";
import { BiometricStatus, useBiometricStatus } from "core/hooks/useBiometricStatus";
import { BiometricLoginModal } from "./BiometricLogin/BiometricLoginModal";
import { useSSO } from "../../../hooks/useSSO/useSSO";
import { SsoChangeCodeModal } from "./Components/SsoChangeCodeModal/SsoChangeCodeModal";

export const useAccountSecurityData = () => {
  const { t } = useTranslation(undefined, { keyPrefix: "accountSecurity.home" });
  const location = useLocation();
  const [triggerReload, setTriggerReload] = useState(false);
  const [triggerBiometricStatus, setTriggerBiometricStatus] = useState(false);
  const { loading, error, secret } = useTwoFactorAuthSetup(triggerReload);
  const { updateClient } = useAuthVersioning();
  const { status, loading: biometricLoading } = useBiometricStatus(triggerBiometricStatus);

  const biometricEnabled = status === BiometricStatus.enabled;
  const { signedUpWithSso, hasSetupSso } = useSSO();

  const handleReloadTrigger = (reload: boolean) => setTriggerReload(reload);
  const handleChangeOrganisation = () => {
    DestinationState.set(location.pathname);
    ClientInfoState.clear();
    updateClient();
  };

  useEffect(() => {
    if (!biometricLoading && status && triggerBiometricStatus) {
      setTriggerBiometricStatus(false);
    }
  }, [biometricLoading, status, triggerBiometricStatus]);

  const accountSecurityModulesData: AccountSecurityModule[] = [
    {
      cta1: {
        id: t("passwordManagement.cta1.id"),
        text: t("passwordManagement.cta1.text"),
        link: t("passwordManagement.cta1.link"),
        eventName: EventNames.START_PASSWORD_CHANGE,
      },
      title: t("passwordManagement.title"),
      description: t("passwordManagement.description"),
      Icon: PasswordManagementIcon,
    },
    {
      cta1: {
        id: t("organizationCode.cta1.id"),
        text: t("organizationCode.cta1.text"),
        link: null,
        eventName: EventNames.CHANGE_ORGANISATION,
        onClick: signedUpWithSso || hasSetupSso ? undefined : handleChangeOrganisation,
        CtaModal: signedUpWithSso || hasSetupSso ? SsoChangeCodeModal : null,
      },
      title: t("organizationCode.title"),
      description: t("organizationCode.description"),
      Icon: OrganizationCodeIcon,
    },
    {
      cta1: {
        ...(biometricEnabled ? t("biometric.cta1.disable", { returnObjects: true }) : t("biometric.cta1.enable", { returnObjects: true })),
        link: null,
        eventName: EventNames.BIOMETRIC_SETUP,
        buttonVariant: biometricEnabled ? "secondary" : "default",
        CtaModal: BiometricLoginModal,
        loading: biometricLoading,
        handleModalDismiss: () => {
          setTriggerBiometricStatus(true);
        },
      },
      title: t("biometric.title"),
      description: t("biometric.description"),
      info: {
        title: t("biometric.info.title"),
        description: t("biometric.info.description"),
      },
      Icon: BiometricLoginIcon,
      hide: !status,
    },
    {
      cta1: {
        id: t("singleSignOn.cta1.id"),
        text: t("singleSignOn.cta1.text"),
        link: t("singleSignOn.cta1.link"),
        eventName: EventNames.START_SINGLE_SIGNON,
      },
      title: t("singleSignOn.title"),
      description: t("singleSignOn.description"),
      info: {
        title: t("singleSignOn.info.title"),
        description: t("singleSignOn.info.description"),
        steps: t("singleSignOn.info.steps", { returnObjects: true }),
        endNote: t("singleSignOn.info.endNote"),
      },
      Icon: SingleSignOnIcon,
    },
    {
      title: t("twoFactorAuth.title"),
      description: t("twoFactorAuth.description"),
      info: {
        title: t("twoFactorAuth.info.title"),
        description: t("twoFactorAuth.description"),
        steps: t("twoFactorAuth.info.steps", { returnObjects: true }),
        stepBannerInsert: t("twoFactorAuth.info.stepBannerInsert"),
        endNote: t("twoFactorAuth.info.endNote"),
      },
      Icon: TwoFactorIcon,
      cta1: {
        id: secret ? t("twoFactorAuth.cta1.setup.id") : t("twoFactorAuth.cta1.deactivate.id"),
        text: secret ? t("twoFactorAuth.cta1.setup.text") : t("twoFactorAuth.cta1.deactivate.text"),
        link: secret ? t("twoFactorAuth.cta1.setup.link") : null,
        loading,
        error: !!error,
        eventName: secret ? EventNames.SETUP_TWO_FA : EventNames.DEACTIVATE_TWO_FA,
        CtaModal: DeactivateModal,
        props: { triggerReload: handleReloadTrigger },
        buttonVariant: secret && !error ? "default" : "secondary",
      },
      ...(!loading &&
        !error &&
        !secret && {
          cta2: {
            id: t("twoFactorAuth.cta2.id"),
            text: t("twoFactorAuth.cta2.text"),
            link: null,
            buttonVariant: "secondary",
            eventName: EventNames.GENERATE_NEW_RECOVERY_CODES,
            CtaModal: RecoveryCodesModal,
            props: { status: "renew" },
          },
        }),
    },
  ];

  return { accountSecurityModulesData };
};
